import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  ButtonSizes,
  ButtonVariants,
  Field,
  Form,
  FormSizes,
  TextInput,
  useFormState,
  FormValidateOn,
} from '@carboledger/ui';
import { COUNTRY_CODES } from '@/config/countries.constants';
import {
  buildCountryOptions,
  isFormDirty,
  EMPTY_LIST,
  REMOVED_LINKS,
  removedLinksOf,
  withRemovedLink,
} from '@/lib/utils';
import { ConfirmDialog } from '@/components/feedback';
import {
  LinkedEntityField,
  AllocationPreferenceField,
  AttachmentsField,
  ReferenceIdField,
  FacilityField,
  TerminalField,
  MaterialField,
  SupplierField,
  TransportNameField,
  VesselNameField,
  GhgEmissionsField,
  GhgEmissionSavingsField,
  RawMaterialTypeField,
  CountryOfOriginField,
  FeedstockCategoryField,
  MultiSiteTransferField,
} from '@/components/common';
import { useTranslations } from '@/hooks';
import { ContractQuantityFields } from './ContractQuantityFields';
import { contractValidation } from '../contractsValidation';
import {
  buildMultiSiteTransferOptions,
  MULTI_SITE_TRANSFER,
} from '../constants/contractFormOptions';
import {
  FACILITY_KINDS,
  buildAllocationPreferenceOptions,
  buildFeedstockCategoryOptions,
  parseFacilityValue,
} from '@/config/formOptions';
import styles from './ContractForm.module.scss';

// facility → terminal → material → unit. Picking a parent drops every stale descendant.
const FACILITY_CLEARS = Object.freeze(['terminalId', 'materialId', 'unit']);
const MATERIAL_CLEARS = Object.freeze(['unit', 'density']);

// Terminal shows only for a site — a back-to-back account has none.
const ContractTerminalField = ({ sites, disabled }) => {
  const { values } = useFormState();
  const facility = parseFacilityValue(values.facilityId);
  if (facility.kind !== FACILITY_KINDS.site) return null;
  return <TerminalField sites={sites} disabled={disabled} />;
};

// Every field the form owns, so a missing key can't become an uncontrolled input.
export const EMPTY_CONTRACT_FORM = Object.freeze({
  idPrefix: '',
  idNumber: '',
  facilityId: '',
  terminalId: '',
  multiSiteTransfer: MULTI_SITE_TRANSFER.no,
  supplierId: '',
  materialId: '',
  deliverBy: '',
  transportName: '',
  vesselName: '',
  contractedQuantity: '',
  unit: '',
  density: '',
  ghgEmissions: '',
  ghgEmissionSavings: '',
  rawMaterialType: '',
  countryOfOrigin: '',
  feedstockCategory: '',
  allocationPreference: [],
  attachments: [],
  // Edit-only: files already on the server + which of their ids Save removes.
  existingAttachments: [],
  removeFileIds: [],
});

/**
 * Props:
 *   title · submitLabel · initialValues · onSubmit · onClose · isSubmitting
 *   isEdit     locks the contract ID — a saved contract keeps the one it was given
 *   options    { facilities, suppliers, rawMaterialTypes }
 *   sites      site view models, for a facility's terminals
 *   materials  material view models, for units and the quantity conversion
 *   linkedShipments  its linked shipments, shown as removable chips
 */
export const ContractForm = ({
  title,
  submitLabel,
  initialValues,
  onSubmit,
  onClose,
  isSubmitting = false,
  isEdit = false,
  options,
  sites,
  materials,
  linkedShipments = EMPTY_LIST,
}) => {
  const t = useTranslations();

  const multiSiteTransferOptions = useMemo(() => buildMultiSiteTransferOptions(t), [t]);
  const feedstockCategoryOptions = useMemo(() => buildFeedstockCategoryOptions(t), [t]);
  // Multi site transfer owns single counting; sending both is a 400 (inboundContractController).
  const allocationPreferenceOptions = useMemo(
    () => buildAllocationPreferenceOptions(t, { includeSingleCounting: false }),
    [t],
  );
  const countryOptions = useMemo(() => buildCountryOptions(COUNTRY_CODES), []);

  // A linked shipment already matches on material, supplier, facility and terminal, so Edit must not move them.
  const pinnedByShipments = linkedShipments.length > 0;

  const [confirmingClose, setConfirmingClose] = useState(false);
  const askBeforeClosing = useCallback(() => setConfirmingClose(true), []);
  const keepEditing = useCallback(() => setConfirmingClose(false), []);

  return (
    <Form
      validateOn={FormValidateOn.change}
      size={FormSizes.large}
      initialValues={initialValues}
      onSubmit={onSubmit}
      className={styles.form}
    >
      {({ values, setValue }) => {
        const requestClose = isFormDirty(values, initialValues) ? askBeforeClosing : onClose;
        return (
          <>
            <Form.Header title={title} onClose={requestClose} closeLabel={t('COMMON_CLOSE')} />

            <Form.Section>
              {/* A 4-up row with one field, so the ID group measures one column. */}
              <Form.Row>
                <ReferenceIdField
                  label={t('FIELD_CONTRACT_ID')}
                  prefixLabel={t('CONTRACTS_FORM_CONTRACT_ID_PREFIX')}
                  numberLabel={t('CONTRACTS_FORM_CONTRACT_ID_NUMBER')}
                  readOnly={isEdit}
                  rules={contractValidation.idNumber}
                />
              </Form.Row>

              <Form.Row>
                <FacilityField
                  options={options.facilities}
                  clears={FACILITY_CLEARS}
                  disabled={pinnedByShipments}
                />

                <ContractTerminalField sites={sites} disabled={pinnedByShipments} />

                {/* TODO: enable once the multi-site-transfer branch is built. */}
                <MultiSiteTransferField options={multiSiteTransferOptions} />

                <SupplierField options={options.suppliers} disabled={pinnedByShipments} />
              </Form.Row>

              <Form.Row>
                <MaterialField
                  materials={materials}
                  clears={MATERIAL_CLEARS}
                  disabled={pinnedByShipments}
                />

                <Field name="deliverBy" required>
                  {({ id, name, value, onChange, onBlur, status, message, required }) => (
                    <TextInput
                      id={id}
                      name={name}
                      // TODO: swap for a DatePicker when the library has one.
                      type="date"
                      label={t('FIELD_DELIVER_BY')}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      status={status}
                      message={message}
                      required={required}
                    />
                  )}
                </Field>

                <TransportNameField />

                <VesselNameField />
              </Form.Row>

              {/* The row too, not just the field — an empty one still costs a section gap. */}
              {linkedShipments.some(
                (link) => !removedLinksOf(values).includes(link.shipmentDbId),
              ) ? (
                <Form.Row>
                  <LinkedShipments
                    linkedShipments={linkedShipments}
                    removed={removedLinksOf(values)}
                    onUnlink={(id) => setValue(REMOVED_LINKS, withRemovedLink(values, id))}
                  />
                </Form.Row>
              ) : null}
            </Form.Section>

            <Form.Section title={t('SECTION_QUANTITY')}>
              <ContractQuantityFields materials={materials} />
            </Form.Section>

            <Form.Section title={t('SECTION_SUSTAINABILITY')}>
              <Form.Row>
                <GhgEmissionsField rules={contractValidation.ghgEmissions} />

                <GhgEmissionSavingsField rules={contractValidation.ghgEmissionSavings} />

                <RawMaterialTypeField options={options.rawMaterialTypes} />

                <CountryOfOriginField options={countryOptions} />
              </Form.Row>

              <Form.Row>
                <FeedstockCategoryField options={feedstockCategoryOptions} />

                <AllocationPreferenceField options={allocationPreferenceOptions} />
              </Form.Row>
            </Form.Section>

            <Form.Section title={t('SECTION_ATTACHMENTS')}>
              <Form.Row columns={1}>
                <AttachmentsField />
              </Form.Row>
            </Form.Section>

            <Form.Footer>
              <Button variant={ButtonVariants.neutral} size={ButtonSizes.sm} onClick={requestClose}>
                {t('COMMON_CANCEL')}
              </Button>
              <Button
                type="submit"
                variant={ButtonVariants.primary}
                size={ButtonSizes.sm}
                disabled={isSubmitting}
              >
                {submitLabel}
              </Button>
            </Form.Footer>

            <ConfirmDialog
              open={confirmingClose}
              title={t('COMMON_DISCARD_TITLE')}
              bodyTitle={t('COMMON_DISCARD_BODY_TITLE')}
              description={t('COMMON_DISCARD_DESCRIPTION')}
              confirmLabel={t('COMMON_YES')}
              onConfirm={onClose}
              onCancel={keepEditing}
            />
          </>
        );
      }}
    </Form>
  );
};

/**
 * Props:
 *   linkedShipments  the shipments already linked to this contract — nothing renders without one
 *   removed          ids taken off in this session; Save is what actually unlinks them
 *   onUnlink         (shipmentDbId) => void
 */
const LinkedShipments = ({ linkedShipments, removed, onUnlink }) => {
  const t = useTranslations();

  const items = useMemo(
    () =>
      linkedShipments
        .filter((link) => !removed.includes(link.shipmentDbId))
        .map((link) => ({
          id: link.id,
          label: link.shipmentId,
          value: link.shipmentDbId,
        })),
    [linkedShipments, removed],
  );

  return (
    <LinkedEntityField label={t('FIELD_LINKED_SHIPMENTS')} items={items} onRemove={onUnlink} />
  );
};
